// The Shipping hub's switch: GET /features "shipping_hub" {enabled} (jobq-api, Phase 1: on for dev, off for prod
// until Mike tries it there). Off = the Shipping page shows exactly its old tabs.
//
// Read here rather than in utils/features.js (that module is shared by other work in flight): one request per
// page load (module-cached promise); a failed request is not cached; any failure, an older API without the key,
// or an odd body reads as OFF, never on.
import { useEffect, useState } from 'react';
import config from '../../config.js';

// The last answer, remembered on this device so a reload opens straight on the right layout instead of flashing
// the old tabs while /features answers (like mobileMode's jobq.phone.lastSwitch). The live answer always wins.
const LAST_KEY = 'jobq.shippingHub.last';
function readLast() {
  try { return window.localStorage.getItem(LAST_KEY) === '1'; } catch (_) { return false; }
}
function writeLast(enabled) {
  try { window.localStorage.setItem(LAST_KEY, enabled ? '1' : '0'); } catch (_) { /* storage blocked */ }
}

/** GET /features body -> whether the hub is on. */
export function parseShippingHub(body) {
  const obj = body && typeof body === 'object' && !Array.isArray(body) ? body : null;
  const sh = obj ? obj.shipping_hub : null;
  return !!(sh && typeof sh === 'object' && !Array.isArray(sh) && sh.enabled === true);
}

// ONE GET /features per page load, shared with the shipping-signing switches (./signingFlags.js): the body, or null
// when the request failed (a failure is not cached: the next caller asks again).
let cache = null;

export function fetchFeaturesBody() {
  if (!cache) {
    const token = {};
    cache = {
      token,
      promise: (async () => {
        try {
          const res = await fetch(`${config.API_BASE_URL}/features`);
          if (!res || !res.ok) throw new Error('no answer');
          return await res.json();
        } catch (_) {
          if (cache && cache.token === token) cache = null;
          return null;
        }
      })(),
    };
  }
  return cache.promise;
}

export async function fetchShippingHubFlag() {
  const body = await fetchFeaturesBody();
  if (body == null) return false;
  const enabled = parseShippingHub(body);
  writeLast(enabled);
  return enabled;
}

/** {loaded, enabled}: until the answer lands, enabled = this device's last answer (off on a first visit). */
export function useShippingHubFlag() {
  const [state, setState] = useState(() => ({ loaded: false, enabled: readLast() }));
  useEffect(() => {
    let alive = true;
    fetchShippingHubFlag().then((enabled) => { if (alive) setState({ loaded: true, enabled }); });
    return () => { alive = false; };
  }, []);
  return state;
}

export function __resetShippingHubFlagForTests() {
  cache = null;
}
